import { RefreshCw } from 'lucide-react'
import { IncomingFlightCard } from './IncomingFlightCard'
import { StrategyOptionCard } from './StrategyOptionCard'
import type { DemoState } from '../hooks/useDemoOrchestrator'
import type { Strategy, DepartureWindow, ScenarioId } from '../data/scenarios'
import { resolveScenarioId } from '../data/scenarios'
import { SCENARIO_METRICS } from '../data/metricsData'

interface ScenarioPanelProps {
  demoState: DemoState
  activeScenarioId: ScenarioId
  selectedStrategy: Strategy | null
  departureWindow: DepartureWindow
  onShowIncoming: () => void
  onShowStrategySelect: () => void
  onSelectStrategy: (s: Strategy) => void
  onAdjustDeparture: (w: DepartureWindow) => void
  onReset: () => void
}

const DEPARTURE_OPTIONS: { value: DepartureWindow; label: string; sub: string }[] = [
  { value: 'morning',   label: '06:00',    sub: 'Early morning' },
  { value: 'midday',    label: '13:00',    sub: 'Midday' },
  { value: 'long_term', label: '4 days',   sub: 'Long-term' },
]

export function ScenarioPanel({
  demoState,
    selectedStrategy,
  departureWindow,
  onShowIncoming,
  onShowStrategySelect,
  onSelectStrategy,
  onAdjustDeparture,
  onReset,
}: ScenarioPanelProps) {
  const isAnimating = demoState === 'animating'

  return (
    <div className="absolute right-4 top-1/2 -translate-y-1/2 w-80 pointer-events-auto z-10 space-y-3">

      {/* ── BASELINE state: prompt to show incoming ── */}
      {demoState === 'baseline' && (
        <div className="bg-slate-900/85 backdrop-blur-md border border-white/10 rounded-xl p-5 shadow-2xl">
          <div className="text-sm text-white/40 uppercase tracking-widest font-semibold mb-2">
            Hangar Intelligence
          </div>
          <h2 className="text-xl font-bold text-white mb-1">Opa-Locka Executive</h2>
          <p className="text-sm text-white/60 mb-4">
            8 aircraft currently parked. Hangar appears at capacity.
          </p>
          <div className="grid grid-cols-3 gap-2 mb-4">
            {[
              { label: 'Occupancy', value: '94%' },
              { label: 'Aircraft', value: '8' },
              { label: 'Free Sqft', value: '~400' },
            ].map((s) => (
              <div key={s.label} className="bg-white/5 rounded-lg p-2.5 text-center">
                <div className="text-lg font-bold text-white">{s.value}</div>
                <div className="text-[10px] text-white/40 uppercase tracking-wide">{s.label}</div>
              </div>
            ))}
          </div>
          <button
            onClick={onShowIncoming}
            className="w-full bg-amber-500 hover:bg-amber-400 text-black font-bold text-sm
                       rounded-lg py-2.5 transition-colors"
          >
            Simulate Inbound Request
          </button>
        </div>
      )}

      {/* ── INCOMING ALERT ── */}
      {demoState === 'incoming_alert' && (
        <div className="bg-slate-900/85 backdrop-blur-md border border-white/10 rounded-xl p-4 shadow-2xl">
          <IncomingFlightCard onContinue={onShowStrategySelect} />
        </div>
      )}

      {/* ── STRATEGY SELECT + RESULT ── */}
      {(demoState === 'strategy_select' || demoState === 'result' || isAnimating) && (
        <div className="bg-slate-900/85 backdrop-blur-md border border-white/10 rounded-xl p-4 shadow-2xl space-y-3">
          <div className="text-xs text-white/40 uppercase tracking-widest font-semibold">
            Choose Stacking Strategy
          </div>

          <StrategyOptionCard
            strategy="max_yield"
            metrics={SCENARIO_METRICS[resolveScenarioId('max_yield', departureWindow)]}
            selected={selectedStrategy === 'max_yield'}
            disabled={isAnimating}
            onSelect={() => onSelectStrategy('max_yield')}
          />

          <StrategyOptionCard
            strategy="op_ease"
            metrics={SCENARIO_METRICS[resolveScenarioId('op_ease', departureWindow)]}
            selected={selectedStrategy === 'op_ease'}
            disabled={isAnimating}
            onSelect={() => onSelectStrategy('op_ease')}
          />

          {/* Departure time picker */}
          <div>
            <div className="text-xs text-white/40 uppercase tracking-widest font-semibold mb-2">
              Hero Departure Time
            </div>
            <div className="grid grid-cols-3 gap-1.5">
              {DEPARTURE_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  disabled={isAnimating}
                  onClick={() => onAdjustDeparture(opt.value)}
                  className={`rounded-lg py-2 px-1 text-center transition-all text-xs
                    ${departureWindow === opt.value
                      ? 'bg-sky-500/30 border border-sky-400/60 text-sky-200'
                      : 'bg-white/5 border border-white/10 text-white/50 hover:text-white hover:bg-white/10'
                    } ${isAnimating ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  <div className="font-bold">{opt.label}</div>
                  <div className="text-[10px] text-current/60 mt-0.5">{opt.sub}</div>
                </button>
              ))}
            </div>
          </div>

          {isAnimating && (
            <div className="flex items-center gap-2 text-xs text-sky-300/80 py-1">
              <div className="w-3 h-3 border border-sky-400/60 border-t-sky-400 rounded-full animate-spin" />
              Calculating optimal positions…
            </div>
          )}
        </div>
      )}

      {/* ── RESET BUTTON (shown after result) ── */}
      {demoState === 'result' && (
        <button
          onClick={onReset}
          className="w-full flex items-center justify-center gap-2 bg-white/8 hover:bg-white/15
                     border border-white/10 text-white/60 hover:text-white text-xs font-medium
                     rounded-xl py-2.5 transition-all pointer-events-auto"
        >
          <RefreshCw className="w-3.5 h-3.5" />
          Reset Demo
        </button>
      )}
    </div>
  )
}
